// ChatDashboard.jsx — Wrapper for Dashboard component.
//
// DESIGN ONLY — no functional changes. Wraps the existing Dashboard
// in a .qxai-dashboard scope so the editorial vault skin
// (src/dashboard/dashboard.css) takes effect without rewriting any
// of the underlying Tailwind UI or touching component logic.
//
// The wrapper's data-theme attribute mirrors the Dashboard's own
// theme state so that toggling the moon/sun icon updates the vault
// palette in lockstep. We observe document.documentElement (where
// Tailwind's dark mode adds/removes either a 'dark' class or a
// data-theme attribute) and re-render with the resolved theme.
import { useEffect, useState } from 'react';
import Dashboard from '../components/Dashboard';
import '../dashboard/dashboard.css';

function resolveTheme() {
  if (typeof document === 'undefined') return 'dark';
  const html = document.documentElement;
  if (html.getAttribute('data-theme') === 'dark') return 'dark';
  if (html.getAttribute('data-theme') === 'light') return 'light';
  if (html.classList.contains('dark')) return 'dark';
  // Fall back to localStorage / system preference matching Dashboard's logic
  const saved = localStorage.getItem('theme');
  if (saved === 'dark' || saved === 'light') return saved;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export default function ChatDashboard() {
  const [theme, setTheme] = useState(resolveTheme);

  useEffect(() => {
    const update = () => setTheme(resolveTheme());

    // Tailwind dark mode triggers off either the .dark class on <html>
    // or [data-theme] attribute. Watch both.
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    });

    // Also catch cross-tab localStorage updates for completeness.
    window.addEventListener('storage', update);

    return () => {
      obs.disconnect();
      window.removeEventListener('storage', update);
    };
  }, []);

  return (
    <div className="qxai-dashboard" data-theme={theme}>
      <Dashboard />
    </div>
  );
}
